import React, { memo } from "react"

import styled from "styled-components"

import {
  elevation,
  IconCrossSmall,
  IconTickSmall,
  sizeNumeric,
  colors,
} from "@izettle/otto"

import { Icon } from "../icon"

import { selectorStates } from "./base"

const THUMB_SIZE = 28
const TRACK_HEIGHT = sizeNumeric.small
const TRACK_WIDTH = sizeNumeric.large
const THUMB_MARGIN = (TRACK_HEIGHT - THUMB_SIZE) / 2
const THUMB_MOVEMENT = TRACK_WIDTH - THUMB_SIZE - THUMB_MARGIN * 2
const FOCUS_PADDING = 6

const SwitchThumbBase = styled.div`
  display: block;
  position: absolute;
  height: ${THUMB_SIZE}px;
  width: ${THUMB_SIZE}px;
  top: ${THUMB_MARGIN}px;
  left: ${THUMB_MARGIN}px;
  transition: transform 0.16s ease-out;
  transform: translateX(0px); /* Needed since this property is animated */

  /* Focus state */
  &:before {
    pointer-events: none;
    content: "";
    transition: opacity 0.12s ease-in-out;
    opacity: 0;
    position: absolute;
    z-index: -1;
    height: calc(100% + ${FOCUS_PADDING}px * 2);
    width: calc(100% + ${FOCUS_PADDING}px * 2);
    border-radius: 50%;
    left: -${FOCUS_PADDING}px;
    top: -${FOCUS_PADDING}px;
    border: 1px solid;
    box-sizing: border-box;
    background-color: ${colors.selector.focus.background};
    border-color: ${colors.selector.focus.border};
  }
`

const SwitchIcon = styled.div`
  position: absolute;
  display: block;
  font-size: 16px;
  font-weight: 800;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  transition: opacity 0.12s ease-in-out;
`

const SwitchIconCross = styled(SwitchIcon).attrs({
  children: <Icon svgIcon={IconCrossSmall} />,
})`
  opacity: 1;
`

const SwitchIconTick = styled(SwitchIcon).attrs({
  children: <Icon svgIcon={IconTickSmall} />,
})`
  opacity: 0;
`

const SwitchThumb = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  background: ${colors.selector.base};
  border-radius: 50%;

  display: flex;
  align-items: center;
  justify-content: center;

  box-shadow: ${elevation.level2};
`

const SwitchTrack = styled.div`
  position: relative;

  flex-shrink: 0;
  height: ${TRACK_HEIGHT}px;
  width: ${TRACK_WIDTH}px;
  border-radius: ${(TRACK_HEIGHT / TRACK_WIDTH) * 50}% / 50%;

  /* Default */
  color: ${colors.icon.disabled};
  transition: background-color 0.12s ease-in-out;
  background-color: ${colors.selector.initial.default};

  ${selectorStates.hover} {
    background-color: ${colors.selector.initial.hover};
  }

  ${selectorStates.active} {
    transition-duration: 0s;
    background-color: ${colors.selector.initial.active};
  }

  /* Selected */
  ${selectorStates.checked} {
    ${SwitchIconTick} {
      opacity: 1;
    }

    ${SwitchIconCross} {
      opacity: 0;
    }

    color: ${colors.selector.selected.default};
    background-color: ${colors.selector.selected.default};

    ${selectorStates.hover} {
      background-color: ${colors.selector.selected.hover};
    }

    ${selectorStates.active} {
      background-color: ${colors.selector.selected.active};
    }

    ${SwitchThumbBase} {
      transform: translateX(${THUMB_MOVEMENT}px);
    }
  }

  /* Disabled */
  ${selectorStates.disabled} {
    background: ${colors.selector.disabled.accent} !important;
    color: ${colors.selector.disabled.accent} !important;

    ${SwitchThumb} {
      box-shadow: none;
    }
  }

  /* Focus */
  ${selectorStates.focus} ${SwitchThumbBase}:before {
    opacity: 0.7;
  }
`

export const SwitchStyles = memo(() => (
  <SwitchTrack>
    <SwitchThumbBase>
      <SwitchThumb>
        <SwitchIconCross />
        <SwitchIconTick />
      </SwitchThumb>
    </SwitchThumbBase>
  </SwitchTrack>
))
